import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useRequestHook } from '../../utils/hooks/hooks';
import { FormInput, InputGroup, InputGroupAddon, InputGroupText } from 'shards-react';
import ReferenceChecker from './components/ReferenceChecker';
import { find } from 'lodash';

const Reference = ({ approved, reference, manualReferenceInput, serviceId }) => {
	const { handleRequest } = useRequestHook();
	const [referenceToShow, setReferenceToShow] = useState(() => reference || '');
	const [toggleDropdown, setToggleDropdown] = useState(() => false);
	const [isValid, setIsValid] = useState(() => false);
	const [isInvalid, setIsInvalid] = useState(() => false);
	const placeHolder = manualReferenceInput ? 'Bitte tragen Sie eine Referenznummer ein.' : 'Bitte wählen Sie eine Referenz aus der Liste aus.';
	const pattern = useMemo(() => '^(?!,)(,?[A-Za-zÄÖÜäöüß\\d-])+$', []);
	const [matchingRefs, setMatchingRefs] = useState(() => []);

	const stopBubbling = useCallback((e) => e.stopPropagation(), []);
	const preventEnterKeySubmit = useCallback((e) => (e.key === 'Enter' ? e.preventDefault() : null), []);
	const handleOnFocus = useCallback(() => (manualReferenceInput ? null : setToggleDropdown(true)), [manualReferenceInput]);
	const handleOnChange = (e) => {
		if (!manualReferenceInput && !toggleDropdown) setToggleDropdown(true);

		return setReferenceToShow(e.target.value.replace(/ /g, ''));
	};

	const removeReference = (reference) => {
		const isMatched = !!find(matchingRefs, { reference });

		if (isMatched) setMatchingRefs((oldMatches) => oldMatches.filter(({ reference: r }) => r !== reference));

		return setReferenceToShow((oldState) => {
			const oldReferences = oldState.split(',');
			const atIndex = oldReferences.indexOf(reference);
			if (atIndex < 0) return;
			oldReferences.splice(atIndex, 1); // remove element at index
			const updatedReferences = oldReferences.join(',');
			return updatedReferences;
		});
	};

	const addReference = (reference) => {
		return setReferenceToShow((oldRef) => {
			const splitedRefs = oldRef.split(',');
			const [lastElement] = splitedRefs.slice(-1);
			const updatedRef = !!find(matchingRefs, { reference: lastElement }) ? `${oldRef},${reference}` : [...splitedRefs.slice(0, -1), reference].join(',');
			return updatedRef;
		});
	};

	useEffect(() => {
		let abort;
		if (!referenceToShow) return;

		const splitedReferences = referenceToShow.split(',');
		const uniqueValues = new Set(splitedReferences).size;
		const duplicateValues = uniqueValues !== splitedReferences.length;
		const isPatternInvalid = !new RegExp(pattern).test(referenceToShow);

		if (manualReferenceInput || isPatternInvalid) {
			const isValid = !duplicateValues && !isPatternInvalid;
			const isInvalid = duplicateValues || isPatternInvalid;

			setIsValid(isValid);
			setIsInvalid(isInvalid);
			return;
		}

		setIsInvalid(true); // add invalid flag while fetching

		handleRequest('post', '/references/check-reference', { serviceId, references: splitedReferences }, ({ referenceList }) => {
			if (abort) return;
			if (!referenceList?.length) return setIsInvalid(true);

			const invalidRefs = splitedReferences.some((reference) => !find(referenceList, { reference }));

			setIsValid(!invalidRefs);
			setIsInvalid(invalidRefs);

			return setMatchingRefs(referenceList);
		});

		return () => {
			abort = true;
		};
	}, [referenceToShow, pattern, serviceId, manualReferenceInput]); //eslint-disable-line

	return (
		<>
			<InputGroup>
				<InputGroupAddon type='prepend'>
					<InputGroupText className='text-dark font-weight-bold'>Referenz:</InputGroupText>
				</InputGroupAddon>
				<FormInput
					type='text'
					id='reference'
					form='detect-types-form'
					autoComplete='false'
					required
					pattern={pattern} // accepts only letters, numbers, dashes. comma as a separator
					title={`Erlaubte Zeichen:
	Groß- oder Kleinbuchstaben, Zahlen, Bindestrich.
	Das Komma trennt die einzelnen Referenzen.
Nicht erlaubte Zeichen:
	Leerzeichen, Tabulator (Tab).`}
					placeholder={placeHolder}
					disabled={approved}
					valid={isValid}
					invalid={isInvalid}
					value={referenceToShow}
					onClick={stopBubbling}
					onFocus={handleOnFocus}
					onChange={(e) => handleOnChange(e)}
					onKeyPress={preventEnterKeySubmit}
				/>
			</InputGroup>
			{toggleDropdown && <ReferenceChecker reference={referenceToShow} addReference={addReference} removeReference={removeReference} matchingRefs={matchingRefs} pattern={pattern} closeDropdown={() => setToggleDropdown(false)} />}
		</>
	);
};

/**
 * Take reference and approved value from redux and return it in components props.
 */
const mapStateToProps = ({ savedDocument: { approved, reference }, userData: { services } }) => {
	const serviceId = window.location.pathname.split('/')[2];
	const [serviceData] = services.filter(({ id }) => id === serviceId);
	const manualReferenceInput = serviceData?.mainModule['-manualReferenceInput'];

	reference = Array.isArray(reference) ? reference.join(',') : reference

	return { approved, reference, serviceId, manualReferenceInput };
};

export default connect(mapStateToProps)(memo(Reference)); //Export component and connect it with redux.
